/**
 * Price calculator embedded into /precos.
 *
 * The visitor selects:
 *   1. Apartment typology (T0–T4 / VS / VM / VL)
 *   2. Frequency of cleaning (semanal, quinzenal, mensal, pontual)
 *   3. Optional extras (lavandaria, consumíveis, etc.)
 *
 * We display:
 *   - Price per cleaning (typology base)
 *   - Estimated monthly total (frequency × per-cleaning price + extras)
 *   - Loyalty discount applied for recurring frequencies
 *   - CTA "Pedir orçamento" → tracks calc_request analytics event and
 *     opens the contact page with the calculation pre-filled.
 *
 * Pricing data comes from /api/public/pricing — the SAME table the
 * admin manages. So changing prices in /admin propagates here
 * automatically.
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Calculator, Sparkles, Calendar, Home, ChevronRight, Check } from "lucide-react";
import { track } from "../lib/analytics";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function eur(v) {
  return new Intl.NumberFormat("pt-PT", {
    style: "currency", currency: "EUR", maximumFractionDigits: 0,
  }).format(v || 0);
}

export default function PricingCalculator() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [aptType, setAptType] = useState("T1");
  const [frequency, setFrequency] = useState("monthly");
  const [extras, setExtras] = useState(new Set());

  useEffect(() => {
    axios.get(`${BACKEND}/api/public/pricing`).then(r => setData(r.data)).catch(() => {});
  }, []);

  const calc = useMemo(() => {
    if (!data) return null;
    const t = data.typologies[aptType];
    const f = data.frequencies.find(x => x.id === frequency);
    if (!t || !f) return null;

    /**
     * Resolve the price of an extra for the currently-selected typology.
     * Some extras (lavandaria, consumiveis) charge a different amount
     * for T0/T1 vs T2/T3 vs T4+, so we honour `price_by_typology` when
     * present. A `null` value means "sob consulta" for THAT size.
     * Returns: { price, onRequest }
     */
    const priceFor = (e) => {
      if (!e) return { price: 0, onRequest: true };
      if (e.price_by_typology && Object.hasOwn(e.price_by_typology, aptType)) {
        const v = e.price_by_typology[aptType];
        if (v === null || v === undefined) return { price: 0, onRequest: true };
        return { price: Number(v), onRequest: false };
      }
      if (e.price_on_request) return { price: 0, onRequest: true };
      return { price: Number(e.price || 0), onRequest: false };
    };

    const perClean = t.normal;
    const extrasTotal = Array.from(extras).reduce((s, id) => {
      const e = data.extras.find(x => x.id === id);
      const p = priceFor(e);
      // Quote-on-request extras don't contribute to the calculator total
      // — they're added later when the admin issues the personalised
      // proposal.
      if (p.onRequest) return s;
      return s + p.price;
    }, 0);

    const cleansPerMonth = f.per_month;
    const monthlyBase = perClean * cleansPerMonth;
    const monthlyDiscount = monthlyBase * (f.discount_pct / 100);
    const monthlyAfterDiscount = monthlyBase - monthlyDiscount;
    const monthlyExtras = extrasTotal * (cleansPerMonth || 1);
    const monthlyTotal = monthlyAfterDiscount + monthlyExtras;

    return {
      typology: t,
      perClean,
      perCleanWithExtras: perClean + extrasTotal,
      extrasTotal,
      cleansPerMonth,
      monthlyBase,
      monthlyDiscount,
      monthlyAfterDiscount,
      monthlyExtras,
      monthlyTotal,
      frequencyLabel: f.label,
      discountPct: f.discount_pct,
      isOccasional: frequency === "occasional",
      hasQuoteExtras: Array.from(extras).some(id => {
        const e = data.extras.find(x => x.id === id);
        return priceFor(e).onRequest;
      }),
      priceFor,
    };
  }, [data, aptType, frequency, extras]);

  function toggleExtra(id) {
    setExtras(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function requestQuote() {
    track("calc_request_quote", {
      apt_type: aptType,
      frequency,
      extras: Array.from(extras),
      monthly_total: calc?.monthlyTotal || 0,
    });
    const summary =
      `Pedido de orçamento\n` +
      `Tipologia: ${calc?.typology?.label}\n` +
      `Frequência: ${calc?.frequencyLabel}\n` +
      `Extras: ${Array.from(extras).join(", ") || "Nenhum"}\n` +
      `Estimativa: ${eur(calc?.monthlyTotal)}/mês`;
    try { sessionStorage.setItem("luxstay-calc-summary", summary); } catch {}
    navigate("/contacto");
  }

  if (!data) {
    return (
      <div className="rounded-2xl border border-[#1a4640] bg-[#062c27]/60 p-8 text-center text-[#a0b2af]">
        A carregar calculadora...
      </div>
    );
  }

  return (
    <section
      className="relative overflow-hidden rounded-3xl border-2 border-[#d4af37]/30 bg-gradient-to-br from-[#062c27] via-[#04201c] to-[#062c27] p-6 sm:p-10"
      data-testid="pricing-calculator"
    >
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.10),transparent_60%)]" />
      <div className="relative">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-[#d4af37]/15 ring-2 ring-[#d4af37]/40 grid place-items-center text-[#d4af37]">
            <Calculator size={18} />
          </div>
          <span className="inline-block text-[10px] uppercase tracking-[0.22em] text-[#d4af37]">
            Calculadora de preços
          </span>
        </div>
        <h2 className="font-display text-2xl sm:text-3xl text-[#fdfbf7]">
          Estime o <span className="italic text-[#d4af37]">seu orçamento</span> em segundos
        </h2>
        <p className="mt-2 text-sm text-[#a0b2af] max-w-2xl">
          Simulação real, com a tabela de preços atual. Sem compromisso.
          Para receber uma proposta detalhada, peça orçamento ao final.
        </p>

        <div className="mt-8 grid lg:grid-cols-[1fr_360px] gap-8">
          {/* LEFT — selectors */}
          <div className="space-y-6">
            {/* Typology */}
            <div data-testid="calc-typology">
              <div className="text-[10px] uppercase tracking-wider text-[#a0b2af] mb-3 inline-flex items-center gap-1.5">
                <Home size={11} /> Tipologia
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Object.entries(data.typologies).map(([code, t]) => (
                  <button key={code}
                          onClick={() => setAptType(code)}
                          data-testid={`calc-typology-${code}`}
                          className={`px-3 py-3 rounded-lg border-2 text-left transition-all ${
                            aptType === code
                              ? "border-[#d4af37] bg-[#d4af37]/10"
                              : "border-[#1a4640] bg-[#041f1b] hover:border-[#d4af37]/40"
                          }`}>
                    <div className="text-xs font-semibold text-[#fdfbf7]">{t.label}</div>
                    <div className="text-[11px] text-[#d4af37] mt-1">{eur(t.normal)} / limpeza</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Frequency */}
            <div data-testid="calc-frequency">
              <div className="text-[10px] uppercase tracking-wider text-[#a0b2af] mb-3 inline-flex items-center gap-1.5">
                <Calendar size={11} /> Frequência
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {data.frequencies.map(f => (
                  <button key={f.id}
                          onClick={() => setFrequency(f.id)}
                          data-testid={`calc-freq-${f.id}`}
                          className={`px-3 py-3 rounded-lg border-2 text-left transition-all ${
                            frequency === f.id
                              ? "border-[#d4af37] bg-[#d4af37]/10"
                              : "border-[#1a4640] bg-[#041f1b] hover:border-[#d4af37]/40"
                          }`}>
                    <div className="text-xs font-semibold text-[#fdfbf7]">{f.label}</div>
                    {f.discount_pct > 0 && (
                      <div className="text-[10px] text-emerald-400 mt-1">−{f.discount_pct}% fidelidade</div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Extras */}
            <div data-testid="calc-extras">
              <div className="text-[10px] uppercase tracking-wider text-[#a0b2af] mb-3 inline-flex items-center gap-1.5">
                <Sparkles size={11} /> Extras (opcional)
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {data.extras.slice(0, 9).map(ex => {
                  const on = extras.has(ex.id);
                  // Per-typology pricing: lavandaria/consumiveis change with size.
                  const p = calc ? calc.priceFor(ex) : { price: 0, onRequest: !!ex.price_on_request };
                  return (
                    <button key={ex.id}
                            onClick={() => toggleExtra(ex.id)}
                            data-testid={`calc-extra-${ex.id}`}
                            className={`px-3 py-2.5 rounded-lg border-2 text-left transition-all ${
                              on ? "border-emerald-500 bg-emerald-500/10"
                                 : "border-[#1a4640] bg-[#041f1b] hover:border-[#d4af37]/40"
                            }`}>
                      <div className="flex items-center gap-2">
                        <span className={`w-3.5 h-3.5 rounded border-2 ${
                          on ? "border-emerald-500 bg-emerald-500" : "border-[#a0b2af]"
                        } grid place-items-center`}>
                          {on && <Check size={9} strokeWidth={3} className="text-[#041f1b]" />}
                        </span>
                        <span className="text-[11px] font-medium text-[#fdfbf7] flex-1 truncate">{ex.name}</span>
                      </div>
                      <div className="text-[10px] text-[#a0b2af] mt-1 ml-5">
                        {p.onRequest
                          ? <span className="italic">sob consulta</span>
                          : `+ ${eur(p.price)}`}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* RIGHT — result panel */}
          <aside className="rounded-2xl border border-[#d4af37]/30 bg-[#04201c] p-6 sticky top-4 self-start"
                 data-testid="calc-result">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#a0b2af]">Resumo</div>
            {calc && (
              <>
                <div className="mt-2 text-xl font-display text-[#fdfbf7]">
                  {calc.typology.label}
                </div>
                <div className="text-xs text-[#a0b2af]">{calc.frequencyLabel}</div>

                <div className="mt-5 space-y-2 text-[12px]">
                  <Row k="Preço por limpeza" v={eur(calc.perClean)} />
                  {calc.extrasTotal > 0 && (
                    <Row k={`Extras (${extras.size})`} v={`+ ${eur(calc.extrasTotal)}`} />
                  )}
                  {!calc.isOccasional && (
                    <>
                      <Row k="Limpezas/mês" v={`${calc.cleansPerMonth.toFixed(2)}×`} />
                      <Row k="Subtotal mensal" v={eur(calc.monthlyBase)} />
                      {calc.discountPct > 0 && (
                        <Row k={`Desconto fidelidade −${calc.discountPct}%`}
                             v={`− ${eur(calc.monthlyDiscount)}`} tone="emerald" />
                      )}
                      {calc.monthlyExtras > 0 && (
                        <Row k="Extras × frequência" v={`+ ${eur(calc.monthlyExtras)}`} />
                      )}
                    </>
                  )}
                </div>

                <div className="mt-5 pt-4 border-t border-[#1a4640]">
                  {calc.isOccasional ? (
                    <>
                      <div className="text-[10px] uppercase tracking-wider text-[#a0b2af]">Estimativa</div>
                      <div className="font-display text-3xl text-[#d4af37]" data-testid="calc-total-occasional">
                        {eur(calc.perCleanWithExtras)}
                      </div>
                      <div className="text-[11px] text-[#a0b2af] mt-1">por serviço pontual</div>
                    </>
                  ) : (
                    <>
                      <div className="text-[10px] uppercase tracking-wider text-[#a0b2af]">Total estimado</div>
                      <div className="font-display text-3xl text-[#d4af37]" data-testid="calc-total-monthly">
                        {eur(calc.monthlyTotal)}
                      </div>
                      <div className="text-[11px] text-[#a0b2af] mt-1">por mês · IVA quando aplicável</div>
                    </>
                  )}
                </div>

                <button onClick={requestQuote}
                        data-testid="calc-request-quote"
                        className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-xs font-semibold uppercase tracking-wider bg-[#d4af37] text-[#041f1b] hover:bg-[#e8c860] transition-all">
                  Pedir orçamento <ChevronRight size={14} />
                </button>
                {calc.hasQuoteExtras && (
                  <p className="mt-3 text-[10px] text-[#e8c860] text-center"
                     data-testid="calc-quote-notice">
                    Selecionou extras sob consulta — o valor final será apresentado na proposta.
                  </p>
                )}
                <p className="mt-1 text-[10px] text-[#a0b2af] text-center italic">
                  Valor indicativo · proposta final personalizada após visita.
                </p>
              </>
            )}
          </aside>
        </div>
      </div>
    </section>
  );
}

function Row({ k, v, tone }) {
  const valueClass = tone === "emerald" ? "text-emerald-300" : "text-[#fdfbf7]";
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-[#a0b2af]">{k}</span>
      <span className={`font-medium ${valueClass}`}>{v}</span>
    </div>
  );
}
